import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  IconButton,
  TextField,
  Button,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  COLORS,
  STATUS_OPTIONS,
  RODZAJ_OPTIONS,
  TRANSPORT_OPTIONS,
  DOKUMENTY_OPTIONS,
  MARKER_GROUPS,
  FINANCE_MARKER_KEYS,
  getFilterableMarkers,
  countActiveFilters,
} from "./dashboardConfig";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 460,
    maxWidth: "100vw",
    background: "#fafafa",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    background: "#fff",
    borderBottom: `1px solid ${COLORS.border}`,
    flexShrink: 0,
  },
  title: {
    flex: 1,
    fontSize: 20,
    fontWeight: 800,
    color: COLORS.primaryDark,
  },
  body: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    background: "#fff",
    borderTop: `1px solid ${COLORS.border}`,
    flexShrink: 0,
  },
  card: {
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
    padding: theme.spacing(1.25, 2),
    background: "transparent",
    border: "none",
    borderBottom: `2px solid ${COLORS.primary}`,
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
  },
  cardHeaderClosed: { borderBottomColor: "transparent" },
  cardTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: 800,
    color: COLORS.primary,
  },
  count: {
    minWidth: 24,
    padding: "2px 8px",
    borderRadius: 12,
    background: COLORS.success,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    textAlign: "center",
  },
  arrow: { color: COLORS.primary, transition: "transform 0.2s" },
  arrowOpen: { transform: "rotate(180deg)" },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.75),
    padding: theme.spacing(2),
  },
  subTitle: {
    margin: theme.spacing(0.5, 0, 0),
    fontSize: 12.5,
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  rangeRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.spacing(1.5) },
  hint: { fontSize: 12, color: "#777", marginTop: -8 },
  chips: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1) },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 12px",
    borderRadius: 16,
    border: "1px solid #bdbdbd",
    background: "#e0e0e0",
    color: "#212121",
    fontSize: 13.5,
    fontWeight: 500,
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "background-color 0.15s, border-color 0.15s",
    "&:hover": { background: "#cfcfcf" },
  },
  chipActive: {
    background: COLORS.success,
    borderColor: COLORS.success,
    color: "#fff",
    "&:hover": { background: "#1b5e20" },
  },
}));

const Card = ({ title, count, defaultOpen = true, children }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={classes.card}>
      <button
        type="button"
        className={`${classes.cardHeader} ${open ? "" : classes.cardHeaderClosed}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={classes.cardTitle}>{title}</span>
        {count > 0 && <span className={classes.count}>{count}</span>}
        <ExpandMoreIcon
          className={`${classes.arrow} ${open ? classes.arrowOpen : ""}`}
          fontSize="small"
        />
      </button>
      {open && <div className={classes.cardBody}>{children}</div>}
    </section>
  );
};

const nonEmpty = (...values) =>
  values.filter((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v))).length;

const FiltersDrawer = ({
  open,
  onClose,
  filters,
  onChange,
  onClear,
  options,
  total,
  statusOptions = STATUS_OPTIONS,
  locationLabel = "Lokalizacja towaru",
}) => {
  const classes = useStyles();

  const text = (key, label, extra = {}) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      autoComplete="off"
      value={filters[key]}
      onChange={(e) => onChange({ [key]: e.target.value })}
      {...extra}
    />
  );

  const multi = (key, label, items) => (
    <Autocomplete
      multiple
      size="small"
      options={items}
      value={filters[key]}
      onChange={(_, value) => onChange({ [key]: value })}
      limitTags={2}
      disableCloseOnSelect
      noOptionsText="Brak opcji"
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );

  const range = (fromKey, toKey, label) => (
    <div>
      <div className={classes.subTitle}>{label}</div>
      <div className={classes.rangeRow} style={{ marginTop: 8 }}>
        <TextField
          label="Od"
          type="date"
          size="small"
          value={filters[fromKey]}
          onChange={(e) => onChange({ [fromKey]: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Do"
          type="date"
          size="small"
          value={filters[toKey]}
          inputProps={{ min: filters[fromKey] || undefined }}
          onChange={(e) => onChange({ [toKey]: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </div>
    </div>
  );

  const toggleMarker = (key) => {
    const current = filters.znaczniki;
    onChange({
      znaczniki: current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key],
    });
  };

  const setGroup = (keys, on) => {
    const rest = filters.znaczniki.filter((k) => !keys.includes(k));
    onChange({ znaczniki: on ? [...rest, ...keys] : rest });
  };

  const activeCount = countActiveFilters(filters);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div className={classes.title}>Filtry</div>
        <Button size="small" onClick={onClear} disabled={activeCount === 0}>
          Wyczyść wszystkie
        </Button>
        <IconButton size="small" onClick={onClose} aria-label="Zamknij filtry">
          <CloseIcon />
        </IconButton>
      </div>

      <div className={classes.body}>
        <Card
          title="Wyszukiwanie"
          count={nonEmpty(
            filters.nrRef,
            filters.nrFaktury,
            filters.importer,
            filters.eksporter,
            filters.nrAuta,
            filters.mrn,
            filters.sad,
            filters.adresatDokumentow,
            filters.dataTekst,
          )}
        >
          {text("nrRef", "Nr ref", {
            multiline: true,
            maxRows: 4,
            helperText: "Kilka numerów po przecinku lub w nowych liniach",
          })}
          {text("nrFaktury", "Nr faktury")}
          {text("importer", "Importer")}
          {text("eksporter", "Eksporter")}
          {text("nrAuta", "Nr auta")}
          {text("mrn", "MRN", {
            multiline: true,
            maxRows: 4,
            helperText: "Kilka numerów po przecinku lub w nowych liniach",
          })}
          <div>
            {text("sad", "SAD", {
              value: filters.sad === "empty" ? "" : filters.sad,
              disabled: filters.sad === "empty",
            })}
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={filters.sad === "empty"}
                  onChange={(e) => onChange({ sad: e.target.checked ? "empty" : "" })}
                />
              }
              label="Tylko bez numeru SAD"
            />
          </div>
          {text("adresatDokumentow", "Adresat dokumentów")}
          {text("dataTekst", "Data utworzenia (tekst)", {
            placeholder: "np. 25-10-2026",
          })}
        </Card>

        <Card
          title="Status i transport"
          count={nonEmpty(
            filters.statusy,
            filters.rodzaje,
            filters.rodzajeTransportu,
            filters.lokalizacje,
            filters.dokumenty,
          )}
        >
          {multi("statusy", "Status", statusOptions)}
          {multi("rodzaje", "Rodzaj odprawy", RODZAJ_OPTIONS)}
          {multi("rodzajeTransportu", "Rodzaj transportu", TRANSPORT_OPTIONS)}
          {multi("lokalizacje", locationLabel, options.lokalizacje)}
          {multi("dokumenty", "Dokumenty przed przybyciem", DOKUMENTY_OPTIONS)}
        </Card>

        <Card
          title="Osoby"
          count={nonEmpty(
            filters.osobaPrzygotowujaca,
            filters.osobaWysylajaca,
            filters.osobaMonitorujaca,
            filters.osobaWeryfikujaca,
          )}
        >
          {multi("osobaPrzygotowujaca", "Osoba przygotowująca", options.osoby)}
          {multi("osobaWysylajaca", "Osoba wysyłająca", options.osoby)}
          {multi("osobaWeryfikujaca", "Osoba weryfikująca", options.osoby)}
          {multi("osobaMonitorujaca", "Osoba monitorująca", options.osoby)}
          <div className={classes.hint}>
            Odprawa pasuje, gdy którakolwiek z wybranych ról zawiera wskazaną osobę.
          </div>
        </Card>

        <Card
          title="Daty"
          count={nonEmpty(
            filters.utworzonoOd || filters.utworzonoDo,
            filters.zakonczonoOd || filters.zakonczonoDo,
            filters.t1Od || filters.t1Do,
          )}
          defaultOpen={false}
        >
          {range("utworzonoOd", "utworzonoDo", "Data utworzenia")}
          {range("zakonczonoOd", "zakonczonoDo", "Data zakończenia")}
          {range("t1Od", "t1Do", "Data wygaśnięcia T1")}
          <div className={classes.hint}>
            Możesz podać tylko jedną z dat, aby ograniczyć zakres z jednej strony.
          </div>
        </Card>

        <Card
          title="Znaczniki"
          count={filters.znaczniki.length + (filters.rewizja ? 1 : 0)}
          defaultOpen={false}
        >
          <div className={classes.hint} style={{ marginTop: 0 }}>
            Odprawa pasuje, gdy ma którykolwiek z zaznaczonych znaczników.
          </div>
          <div>
            <div className={classes.chips}>
              <button
                type="button"
                className={`${classes.chip} ${filters.rewizja ? classes.chipActive : ""}`}
                onClick={() => onChange({ rewizja: !filters.rewizja })}
                aria-pressed={filters.rewizja}
              >
                Rewizja
              </button>
            </div>
          </div>
          {MARKER_GROUPS.map((group) => {
            const markers = getFilterableMarkers(group.id);
            if (markers.length === 0) return null;
            return (
              <div key={group.id}>
                <div
                  className={classes.subTitle}
                  style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}
                >
                  <span style={{ flex: 1 }}>{group.title}</span>
                  {group.id === "finanse" && (
                    <Tooltip title="Zaznacz wszystkie znaczniki działu finansowego">
                      <Button
                        size="small"
                        onClick={() => setGroup(FINANCE_MARKER_KEYS, true)}
                        style={{ textTransform: "none", padding: "0 6px" }}
                      >
                        Zaznacz wszystkie
                      </Button>
                    </Tooltip>
                  )}
                </div>
                <div className={classes.chips}>
                  {markers.map((marker) => {
                    const active = filters.znaczniki.includes(marker.filterKey);
                    return (
                      <button
                        key={marker.filterKey}
                        type="button"
                        className={`${classes.chip} ${active ? classes.chipActive : ""}`}
                        onClick={() => toggleMarker(marker.filterKey)}
                        aria-pressed={active}
                      >
                        {marker.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </Card>
      </div>

      <div className={classes.footer}>
        <span style={{ color: COLORS.textMuted, fontSize: 14 }}>
          Wyników: <strong style={{ color: "#212121" }}>{total}</strong>
        </span>
        <Button variant="contained" color="primary" onClick={onClose}>
          Pokaż wyniki
        </Button>
      </div>
    </Drawer>
  );
};

export default FiltersDrawer;
